import { useMemo, useState } from "react";
import {
  CATALOG_MUSCLE_SLUGS,
  DAY_MS,
  DEFAULT_WEEK_START_DAY,
  bucketTagVolumeByWeek,
  type CatalogMuscleSlug,
  type Exercise,
} from "@heraion/shared";

import type { ResourceCache } from "../Logger/state/asyncListCache";
import { usePersistedCopy } from "../usePersistedCopy";
import { PROGRESS_WEEK_BARS } from "./progressWindow";
import {
  useChartWorkouts,
  type UseChartWorkoutsOptions,
} from "./useChartWorkouts";

export type UseTagVolumeRecordsOptions = UseChartWorkoutsOptions & {
  // PG-57: the catalog the host already holds, the tags' owner. Read by
  // exercise key, so every session in the window counts, the old ones too.
  catalog: ReadonlyArray<Exercise> | null;
  // The athlete's week start (Profile.weekStartDay), the Rhythm's weeks.
  weekStartDay?: number;
  // #397: the athlete's own hosts keep the weeks on disk for offline. The
  // weeks, not the workouts: one row per catalog muscle, PROGRESS_WEEK_BARS
  // numbers each, so the copy is bounded by construction.
  cache?: ResourceCache<TagWeeks>;
};

/** One muscle's volume per calendar week, oldest first, this week last. */
type MuscleWeeks = { muscle: CatalogMuscleSlug; weeks: number[] };

export type TagWeeks = {
  /** The instant the weeks end at, to name them. */
  nowMs: number;
  weekStartDay: number | undefined;
  /** Every catalog muscle with some volume in the weeks, catalog order. */
  series: MuscleWeeks[];
  /** Older sessions exist that the weeks above did not load. */
  capped: boolean;
};

export type UseTagVolumeRecordsResult = ReturnType<typeof useChartWorkouts> & {
  /** null until the chart workouts arrive. */
  tagWeeks: TagWeeks | null;
};

// Volume-by-tag records, the data behind `ProgressRecordsCard`, shared so
// mobile and web-rnw drive the SAME card from the SAME logic
// (lift/progress-tag-volume-web split `useChartWorkouts` out first; this hook
// is its natural sibling). PG-64 fetta 2: muscle tags only, one series per
// catalog muscle (a legacy spelling folds into its catalog slug inside
// `bucketTagVolumeByWeek`), bucketed on the last PROGRESS_WEEK_BARS calendar
// weeks.
export function useTagVolumeRecords(
  opts: UseTagVolumeRecordsOptions,
): UseTagVolumeRecordsResult {
  const { catalog, weekStartDay, cache, ...chartOpts } = opts;
  // One extra week covers the partial current week and any weekStartDay.
  // Fixed at mount so the load keeps one window.
  const [sinceMs] = useState(
    () => Date.now() - (PROGRESS_WEEK_BARS + 1) * 7 * DAY_MS,
  );
  const chart = useChartWorkouts({ ...chartOpts, sinceMs });
  const fresh = useMemo<TagWeeks | null>(() => {
    if (chart.chartWorkouts === null) return null;
    const workouts = chart.chartWorkouts;
    const nowMs = Date.now();
    const series = CATALOG_MUSCLE_SLUGS.map((muscle) => ({
      muscle,
      weeks: bucketTagVolumeByWeek(
        workouts,
        muscle,
        PROGRESS_WEEK_BARS,
        weekStartDay ?? DEFAULT_WEEK_START_DAY,
        { nowMs, catalog: catalog ?? [] },
      ),
    })).filter((s) => s.weeks.some((v) => v > 0));
    return { nowMs, weekStartDay, series, capped: chart.chartCapped };
  }, [chart.chartWorkouts, chart.chartCapped, catalog, weekStartDay]);
  const tagWeeks = usePersistedCopy(cache, fresh);
  return { ...chart, tagWeeks };
}
